import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import './AdminStyles.css';

function AddUser() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    role: 'manager',
    isActive: true
  });
  const [selectedPages, setSelectedPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // All available pages in admin sidebar
  const availablePages = [
    { id: 'dashboard', name: 'Dashboard' },
    { id: 'classes', name: 'Classes' },
    { id: 'chat', name: 'Chat' },
    { id: 'add-user', name: 'Add User' },
    { id: 'user-role', name: 'User Role' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const togglePage = (pageId) => {
    setSelectedPages(prev => {
      if (prev.includes(pageId)) {
        return prev.filter(id => id !== pageId);
      } else {
        return [...prev, pageId];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.username.trim()) {
      setError('Username is required');
      return;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }
    if (!formData.password) {
      setError('Password is required');
      return;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (selectedPages.length === 0) {
      setError('Please select at least one page permission');
      return;
    }

    try {
      setLoading(true);

      // Check if username already exists
      const { data: existingUser } = await supabase
        .from('user_roles')
        .select('id')
        .eq('username', formData.username)
        .maybeSingle();

      if (existingUser) {
        setError('Username already exists');
        return;
      }

      // Save user directly to user_roles table (no Supabase Auth)
      const { error: roleError } = await supabase
        .from('user_roles')
        .insert({
          username: formData.username,
          email: formData.email,
          password: formData.password, // Store password directly
          full_name: formData.fullName || null,
          role: formData.role,
          page_permissions: selectedPages,
          is_active: true
          // Note: user_id is not included - it's nullable in the table
        });

      if (roleError) throw roleError;

      setSuccess('User created successfully!');
      
      // Reset form
      setFormData({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        fullName: '',
        role: 'manager',
        isActive: true
      });
      setSelectedPages([]);
    } catch (error) {
      console.error('Error creating user:', error);
      setError(error.message || 'Failed to create user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-layout">
      <div className="admin-main-content">
        <div className="main-header">
          <h1>Add User</h1>
        </div>
        <div className="main-content-body">
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <form onSubmit={handleSubmit} className="form-card">
            <h2>User Information</h2>
            
            <div className="form-group">
              <label>Username *</label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
                placeholder="Enter username"
              />
            </div>

            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="Enter email"
              />
            </div>

            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                placeholder="Enter full name"
              />
            </div>

            <div className="form-group">
              <label>Password *</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                placeholder="Enter password (min 6 characters)"
                minLength={6}
              />
            </div>

            <div className="form-group">
              <label>Confirm Password *</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
                placeholder="Confirm password"
              />
            </div>

            <div className="form-group">
              <label>Role</label>
              <select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
              >
                <option value="head_teacher">Head Teacher</option>
                <option value="manager">Manager</option>
              </select>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                />
                Active
              </label>
            </div>

            <div className="form-group">
              <label>Page Permissions *</label>
              <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                Select the pages this user can access:
              </p>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                gap: '10px',
                padding: '15px',
                border: '1px solid #e9ecef',
                borderRadius: '4px',
                backgroundColor: '#f8f9fa'
              }}>
                {availablePages.map(page => (
                  <label 
                    key={page.id}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      cursor: 'pointer',
                      padding: '8px',
                      borderRadius: '4px',
                      backgroundColor: selectedPages.includes(page.id) ? '#E3F2FD' : 'white',
                      border: selectedPages.includes(page.id) ? '2px solid #2196F3' : '1px solid #e9ecef'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedPages.includes(page.id)}
                      onChange={() => togglePage(page.id)}
                    />
                    <span>{page.name}</span>
                  </label>
                ))}
              </div>
              {selectedPages.length > 0 && (
                <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                  Selected: {selectedPages.length} page(s)
                </p>
              )}
            </div>

            <button type="submit" disabled={loading}>
              {loading ? 'Creating User...' : 'Create User'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AddUser;
